import React, {useState} from "react";
import * as yup from "yup";
import {Modal} from "reactstrap";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {components} from "react-select";

import {errorMessage, invalidEmailFormat, requiredError} from "../../constants/errorMessages";
import mailWithArrow from "assets/images/ic-mail-with-arrow.svg";
import {useChosenPatientContext} from "pages/Dashboard/tableContext";
import toastr from 'components/toastr';
import PatientsManagementService from "services/patientsManagement";
import Label from "components/Label/labelWithError";
import Select from "components/Select";
import strictValidateEmail from "../validateFunctions/strictEmailValidate";

const { Option: OptionComponent } = components;

const initialState = {
    emails: '',
    template: 0
};

const TemplatesToSendGPOptions = [
    {label: 'Private GP Letter', value: 0},
    {label: 'Private GP Letter - action required', value: 1},
    {label: 'Private GP Letter – urgent action required', value: 2},
    {label: 'Mental health', value: 3},
];

const parseCommaSeparatedEmails = (str) => str?.replace(/\s+/g, '').split(',');

const schema = yup.object().shape({
    emails: yup.string()
        .trim()
        .required(requiredError)
        .test(
            'validEmails',
            invalidEmailFormat,
            (value) => parseCommaSeparatedEmails(value)?.every(strictValidateEmail)
        ),
    template: yup.number()
        .required(requiredError),
});


const ConsultationDetails = ({ value: appointment }) => {
    const user = useChosenPatientContext();

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSendConsultation = async (data) => {
        setIsLoading(true);

        try {
            await PatientsManagementService.sendConsultationToEmails(
                appointment.consultation.id,
                {
                    ...data,
                    emails: parseCommaSeparatedEmails(data.emails),
                }
            );

            toastr.success('Consultation has been successfully sent');
            setModalIsOpen(false);
        } finally {
            setIsLoading(false);
        }
    }

    const handleClick = () => {
        if (!user?.patient.isInfoShared) {
            return toastr.warning("The patient does not agree to share info with GP")
        }

        setModalIsOpen(true)
    }

    return (
        <div className="d-flex align-items-baseline">
            <a
                href={appointment.consultation.file.link}
                target="_blank"
                rel="noreferrer"
                className="see-details"
            >
                See details
            </a>
            <img
                src={mailWithArrow}
                alt=""
                className="ml-5 cursor-pointer"
                onClick={handleClick}
            />
            <Modal isOpen={modalIsOpen} centered>
                <Formik
                    initialValues={initialState}
                    validationSchema={schema}
                    onSubmit={handleSendConsultation}
                >
                    {({setFieldValue}) => (
                        <Form className="p-40px h-100 w-100">
                            <label className="d-block font-size-22 font-weight-normal">Send a discharge letter</label>
                            <Label text="Select template" htmlFor="template" className="mt-2"/>
                            <Select
                                options={TemplatesToSendGPOptions}
                                defaultValue={TemplatesToSendGPOptions[0]}
                                components={{
                                    IndicatorSeparator: () => null,
                                    Option: (props) => (
                                        <div className="search-options-big">
                                            <OptionComponent {...props}/>
                                        </div>
                                    )
                                }}
                                onChange={(option) => setFieldValue("template", option.value)}
                            />
                            <ErrorMessage name="template" render={errorMessage}/>

                            <section className="mt-4">
                                <Label htmlFor="emails" text="Email addresses"/>
                                <Field
                                    type="text"
                                    name="emails"
                                    className="form-control w-100"
                                    placeholder="Enter comma separated email addresses"
                                />
                                <div className="position-absolute">
                                    <ErrorMessage name="emails" className="position-relative" render={errorMessage}/>
                                </div>
                            </section>

                            <section
                                className="d-flex flex-column flex-sm-row w-100 justify-content-center modal-actions coupon-form-wrapper-buttons"
                            >
                                <button
                                    type="submit"
                                    className="btn btn-primary w-100"
                                    disabled={isLoading}
                                >
                                    Send
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger w-100"
                                    onClick={() => setModalIsOpen(false)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                            </section>
                        </Form>
                    )}
                </Formik>
            </Modal>
        </div>
    );
};

export default ConsultationDetails;
